import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Map,
  Code,
  Users,
  Printer,
  Target,
  Cpu,
  Zap,
  ChevronRight,
  Play,
  Pause,
} from "lucide-react";

const ServicesOverview = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [activeService, setActiveService] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [direction, setDirection] = useState(0);

  const services = [
    {
      id: 1,
      icon: <Map className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />,
      title: "GIS Services",
      description:
        "Advanced geospatial solutions including drone surveys, forest diversions, and environmental mapping with precision accuracy.",
      shortDesc: "Precision mapping and spatial intelligence",
      color: "#0071BD",
      gradient: "from-blue-500 to-cyan-500",
      features: [
        "Forest Diversion Surveys",
        "Drone & Aerial Mapping",
        "Mining Plan Development",
        "Wildlife Management",
        "Environmental Clearance",
        "GPS/DGPS Surveys",
        "Remote Sensing Analysis",
      ],
      stats: [
        { value: "98%", label: "Accuracy Rate" },
        { value: "500+", label: "Projects" },
        { value: "24h", label: "Response Time" },
      ],
    },
    {
      id: 2,
      icon: <Code className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />,
      title: "Software Services",
      description:
        "Custom software development, web applications, and IT solutions tailored to your business needs with cutting-edge technology.",
      shortDesc: "Custom software and IT solutions",
      color: "#6DA512",
      gradient: "from-green-500 to-emerald-500",
      features: [
        "Website Development",
        "Mobile Applications",
        "Enterprise Software",
        "IT Infrastructure",
        "Maintenance & Support",
        "Cloud Solutions",
        "Database Management",
      ],
      stats: [
        { value: "150+", label: "Apps Built" },
        { value: "99.9%", label: "Uptime" },
        { value: "50+", label: "Clients" },
      ],
    },
    {
      id: 3,
      icon: <Users className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />,
      title: "Consulting Services",
      description:
        "Expert consulting services across environmental, mining, and IT sectors with strategic planning and feasibility studies.",
      shortDesc: "Strategic business consulting",
      color: "#8B5CF6",
      gradient: "from-purple-500 to-pink-500",
      features: [
        "Environmental Consulting",
        "Mining Strategy",
        "IT & GIS Planning",
        "Project Feasibility",
        "Regulatory Compliance",
        "Risk Assessment",
        "Sustainability Planning",
      ],
      stats: [
        { value: "200+", label: "Consultations" },
        { value: "95%", label: "Success Rate" },
        { value: "15+", label: "Industries" },
      ],
    },
    {
      id: 4,
      icon: <Printer className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />,
      title: "Printing & Scanning",
      description:
        "High-quality technical printing, large-format outputs, and professional scanning services for all your documentation needs.",
      shortDesc: "Professional printing & digitization",
      color: "#F59E0B",
      gradient: "from-orange-500 to-red-500",
      features: [
        "Large-Format Printing",
        "CAD & Technical Drawings",
        "Document Scanning",
        "Digital Archiving",
        "Custom Print Solutions",
        "Quality Assurance",
        "Fast Turnaround",
      ],
      stats: [
        { value: "10K+", label: "Documents" },
        { value: "A0-A4", label: "All Sizes" },
        { value: "48h", label: "Delivery" },
      ],
    },
  ];

  // Auto-rotate services with direction tracking
  React.useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setDirection(1);
      setActiveService((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoPlay, services.length]);

  const handleServiceChange = (index) => {
    const newDirection = index > activeService ? 1 : -1;
    setDirection(newDirection);
    setActiveService(index);
    setAutoPlay(false);
  };

  // Responsive container variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  // Responsive item variants
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  // Responsive slide variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
      scale: 0.98,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 25,
        duration: 0.6,
      },
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      scale: 0.98,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 25,
        duration: 0.4,
      },
    }),
  };

  // Feature item animation
  const featureVariants = {
    hidden: {
      opacity: 0,
      x: -20,
    },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 150,
        damping: 15,
        delay: i * 0.05,
        duration: 0.4,
      },
    }),
  };

  // Stats item animation
  const statVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 15,
    },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12,
        delay: 0.3 + i * 0.08,
        duration: 0.4,
      },
    }),
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-8 sm:py-12 lg:py-16 bg-white overflow-hidden"
    >
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-gray-50/80 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-gray-50/80 to-transparent"></div>

        {/* Responsive floating elements */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            x: [0, 8, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-10 left-4 sm:top-20 sm:left-10 w-4 h-4 sm:w-6 sm:h-6 bg-brandBlue/15 rounded-full blur-sm"
        />
        <motion.div
          animate={{
            y: [0, 15, 0],
            x: [0, -12, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className="absolute top-20 right-8 sm:top-40 sm:right-20 w-3 h-3 sm:w-4 sm:h-4 bg-brandGreen/15 rounded-full blur-sm"
        />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Responsive Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            duration: 0.8,
          }}
          className="text-center max-w-4xl mx-auto mb-8 sm:mb-12 lg:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center px-3 py-1 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 border border-brandBlue/20 mb-3 sm:mb-4"
          >
            <Cpu className="w-3 h-3 sm:w-4 sm:h-4 text-brandBlue mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm font-semibold text-brandText">
              Our Services
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
              type: "spring",
              stiffness: 80,
              damping: 20,
              delay: 0.3,
              duration: 0.8,
            }}
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-4 sm:mb-6 lg:mb-8 font-lexend leading-tight"
          >
            <span className="block sm:inline">Expert </span>
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={
                isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }
              }
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 0.5,
                duration: 0.6,
              }}
              className="block sm:inline bg-gradient-to-r from-brandBlue to-brandGreen bg-clip-text text-transparent mt-1 sm:mt-0 sm:ml-2"
            >
              Solutions
            </motion.span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base sm:text-lg lg:text-xl text-secondaryTextColor leading-relaxed px-2 sm:px-0"
          >
            Comprehensive services designed to transform your vision into
            reality with innovation and precision.
          </motion.p>
        </motion.div>

        {/* Main Content - Fully Responsive Layout */}
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-12 items-start">
          {/* Service Navigation - Mobile Horizontal Scroll */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="w-full lg:w-1/3 xl:w-1/4"
          >
            {/* Mobile Horizontal Scroll Container */}
            <div className="lg:hidden overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
              <div className="flex space-x-4 min-w-max">
                {services.map((service, index) => (
                  <motion.button
                    key={service.id}
                    variants={itemVariants}
                    custom={index}
                    onClick={() => handleServiceChange(index)}
                    className={`flex-shrink-0 w-64 p-4 rounded-xl text-left transition-all duration-500 group ${
                      activeService === index
                        ? "bg-white shadow-lg border-2"
                        : "bg-gray-50/60 hover:bg-white hover:shadow-md border border-transparent"
                    } backdrop-blur-sm`}
                    style={{
                      borderColor:
                        activeService === index ? service.color : "transparent",
                    }}
                    whileHover={{
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 300, damping: 20 },
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <motion.div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-white transition-all duration-500 ${
                          activeService === index
                            ? "scale-105 shadow-lg"
                            : "shadow-md"
                        }`}
                        style={{
                          background:
                            activeService === index
                              ? `linear-gradient(135deg, ${service.color}, ${service.color}dd)`
                              : `linear-gradient(135deg, ${service.color}40, ${service.color}30)`,
                        }}
                      >
                        {service.icon}
                      </motion.div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-base font-bold font-lexend transition-colors duration-500 truncate ${
                          activeService === index
                            ? "text-primaryTextColor"
                            : "text-secondaryTextColor"
                        }`}>
                          {service.title}
                        </h3>
                        <p className={`text-xs mt-1 transition-colors duration-500 text-ellipsis overflow-hidden ${
                          activeService === index
                            ? "text-secondaryTextColor"
                            : "text-gray-500"
                        }`}>
                          {service.shortDesc}
                        </p>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Desktop Vertical List */}
            <div className="hidden lg:block space-y-3">
              {services.map((service, index) => (
                <motion.button
                  key={service.id}
                  variants={itemVariants}
                  custom={index}
                  onClick={() => handleServiceChange(index)}
                  className={`w-full p-4 sm:p-5 rounded-xl text-left transition-all duration-500 group ${
                    activeService === index
                      ? "bg-white shadow-lg border-2 scale-[1.02]"
                      : "bg-gray-50/60 hover:bg-white hover:shadow-md border border-transparent hover:scale-[1.01]"
                  } backdrop-blur-sm`}
                  style={{
                    borderColor:
                      activeService === index ? service.color : "transparent",
                  }}
                  whileHover={{
                    scale: activeService === index ? 1.02 : 1.01,
                    transition: { type: "spring", stiffness: 300, damping: 20 },
                  }}
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <motion.div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all duration-500 ${
                        activeService === index
                          ? "scale-105 shadow-lg"
                          : "shadow-md"
                      }`}
                      style={{
                        background:
                          activeService === index
                            ? `linear-gradient(135deg, ${service.color}, ${service.color}dd)`
                            : `linear-gradient(135deg, ${service.color}40, ${service.color}30)`,
                      }}
                    >
                      {service.icon}
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-base sm:text-lg font-bold font-lexend transition-colors duration-500 truncate ${
                        activeService === index
                          ? "text-primaryTextColor"
                          : "text-secondaryTextColor"
                      }`}>
                        {service.title}
                      </h3>
                      <p className={`text-xs sm:text-sm mt-1 transition-colors duration-500 text-ellipsis overflow-hidden ${
                        activeService === index
                          ? "text-secondaryTextColor"
                          : "text-gray-500"
                      }`}>
                        {service.shortDesc}
                      </p>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-500 flex-shrink-0 ${
                        activeService === index
                          ? "text-brandBlue"
                          : "text-gray-400 group-hover:text-brandBlue"
                      }`}
                    />
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Service Details - Responsive Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{
              type: "spring",
              stiffness: 80,
              damping: 20,
              delay: 0.4,
              duration: 0.8,
            }}
            className="w-full lg:w-2/3 xl:w-3/4"
          >
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeService}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl border border-gray-200/80 overflow-hidden backdrop-blur-sm"
              >
                <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
                  {/* Service Header - Responsive */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 lg:mb-8 space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <motion.div
                        className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg sm:shadow-xl"
                        style={{
                          background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}dd)`,
                        }}
                        animate={{
                          scale: [1, 1.05, 1],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        {services[activeService].icon}
                      </motion.div>
                      <div>
                        <motion.h3
                          className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-primaryTextColor font-lexend"
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          {services[activeService].title}
                        </motion.h3>
                        <motion.p
                          className="text-sm sm:text-base lg:text-lg xl:text-xl text-secondaryTextColor mt-1 sm:mt-2"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                        >
                          {services[activeService].shortDesc}
                        </motion.p>
                      </div>
                    </div>

                    {/* Auto-play control */}
                    <motion.button
                      onClick={() => setAutoPlay(!autoPlay)}
                      className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-300 self-start sm:self-auto"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {autoPlay ? (
                        <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                      ) : (
                        <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                    </motion.button>
                  </div>

                  {/* Description */}
                  <motion.p
                    className="text-sm sm:text-base lg:text-lg text-secondaryTextColor leading-relaxed mb-4 sm:mb-6 lg:mb-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    {services[activeService].description}
                  </motion.p>

                  {/* Features and Stats Grid */}
                  <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                    {/* Features */}
                    <div>
                      <motion.h4
                        className="text-lg sm:text-xl font-bold text-primaryTextColor mb-3 sm:mb-4 lg:mb-6 font-lexend flex items-center"
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-brandBlue mr-2" />
                        Key Features
                      </motion.h4>
                      <div className="space-y-2 sm:space-y-3">
                        {services[activeService].features.map(
                          (feature, index) => (
                            <motion.div
                              key={index}
                              custom={index}
                              variants={featureVariants}
                              initial="hidden"
                              animate="visible"
                              className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-gray-50 to-white hover:shadow-md transition-all duration-300 group cursor-pointer"
                              whileHover={{
                                scale: 1.02,
                                x: 3,
                              }}
                            >
                              <motion.div
                                className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brandBlue rounded-full flex-shrink-0"
                                whileHover={{ scale: 1.3 }}
                              />
                              <span className="text-xs sm:text-sm text-secondaryTextColor font-medium">
                                {feature}
                              </span>
                            </motion.div>
                          )
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div>
                      <motion.h4
                        className="text-lg sm:text-xl font-bold text-primaryTextColor mb-3 sm:mb-4 lg:mb-6 font-lexend flex items-center"
                        initial={{ opacity: 0, x: 15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-brandGreen mr-2" />
                        Performance Metrics
                      </motion.h4>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                        {services[activeService].stats.map((stat, index) => (
                          <motion.div
                            key={index}
                            custom={index}
                            variants={statVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-center p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                            whileHover={{
                              scale: 1.03,
                              y: -1,
                            }}
                          >
                            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-primaryTextColor mb-1">
                              {stat.value}
                            </div>
                            <div className="text-xs sm:text-sm text-secondaryTextColor font-medium leading-tight">
                              {stat.label}
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: "spring",
                          stiffness: 150,
                          damping: 15,
                          delay: 0.6,
                          duration: 0.5,
                        }}
                        className="w-full mt-4 sm:mt-6 py-3 sm:py-4 text-white font-semibold rounded-lg sm:rounded-xl hover:shadow-xl transition-all duration-300 transform text-sm sm:text-base lg:text-lg relative overflow-hidden group"
                        style={{
                          background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}dd)`,
                        }}
                        whileHover={{
                          scale: 1.02,
                          y: -1,
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="relative z-10">
                          Explore {services[activeService].title}
                        </span>
                        <motion.div
                          className="absolute inset-0 bg-white/20"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "100%" }}
                          transition={{ duration: 0.5 }}
                        />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Enhanced Bottom Navigation Dots - Always Visible */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            delay: 0.8,
            duration: 0.6,
          }}
          className="flex justify-center space-x-2 sm:space-x-3 mt-6 sm:mt-8 lg:mt-12"
        >
          {services.map((service, index) => (
            <motion.button
              key={index}
              onClick={() => handleServiceChange(index)}
              className={`relative rounded-full transition-all duration-300 ${
                activeService === index ? "scale-110" : "hover:scale-105"
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <div
                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                  activeService === index
                    ? "bg-brandBlue"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
              {activeService === index && (
                <motion.div
                  className="absolute inset-0 border border-brandBlue rounded-full"
                  initial={{ scale: 1.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                />
              )}
            </motion.button>
          ))}
        </motion.div>
      </div>

      {/* Custom scrollbar hide for mobile */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default ServicesOverview;






// import React, { useRef, useState } from "react";
// import { motion, useInView, AnimatePresence } from "framer-motion";
// import {
//   Map,
//   Code,
//   Users,
//   Printer,
//   Target,
//   Cpu,
//   Zap,
//   ChevronRight,
//   Play,
//   Pause,
// } from "lucide-react";

// const ServicesOverview = () => {
//   const sectionRef = useRef(null);
//   const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
//   const [activeService, setActiveService] = useState(0);
//   const [autoPlay, setAutoPlay] = useState(true);
//   const [direction, setDirection] = useState(0);

//   const services = [
//     {
//       id: 1,
//       icon: <Map className="w-8 h-8" />,
//       title: "GIS Services",
//       description:
//         "Advanced geospatial solutions including drone surveys, forest diversions, and environmental mapping with precision accuracy.",
//       shortDesc: "Precision mapping and spatial intelligence",
//       color: "#0071BD",
//       gradient: "from-blue-500 to-cyan-500",
//       features: [
//         "Forest Diversion Surveys",
//         "Drone & Aerial Mapping",
//         "Mining Plan Development",
//         "Wildlife Management",
//         "Environmental Clearance",
//         "GPS/DGPS Surveys",
//         "Remote Sensing Analysis",
//       ],
//       stats: [
//         { value: "98%", label: "Accuracy Rate" },
//         { value: "500+", label: "Projects" },
//         { value: "24h", label: "Response Time" },
//       ],
//     },
//     {
//       id: 2,
//       icon: <Code className="w-8 h-8" />,
//       title: "Software Services",
//       description:
//         "Custom software development, web applications, and IT solutions tailored to your business needs with cutting-edge technology.",
//       shortDesc: "Custom software and IT solutions",
//       color: "#6DA512",
//       gradient: "from-green-500 to-emerald-500",
//       features: [
//         "Website Development",
//         "Mobile Applications",
//         "Enterprise Software",
//         "IT Infrastructure",
//         "Maintenance & Support",
//         "Cloud Solutions",
//         "Database Management",
//       ],
//       stats: [
//         { value: "150+", label: "Apps Built" },
//         { value: "99.9%", label: "Uptime" },
//         { value: "50+", label: "Clients" },
//       ],
//     },
//     {
//       id: 3,
//       icon: <Users className="w-8 h-8" />,
//       title: "Consulting Services",
//       description:
//         "Expert consulting services across environmental, mining, and IT sectors with strategic planning and feasibility studies.",
//       shortDesc: "Strategic business consulting",
//       color: "#8B5CF6",
//       gradient: "from-purple-500 to-pink-500",
//       features: [
//         "Environmental Consulting",
//         "Mining Strategy",
//         "IT & GIS Planning",
//         "Project Feasibility",
//         "Regulatory Compliance",
//         "Risk Assessment",
//         "Sustainability Planning",
//       ],
//       stats: [
//         { value: "200+", label: "Consultations" },
//         { value: "95%", label: "Success Rate" },
//         { value: "15+", label: "Industries" },
//       ],
//     },
//     {
//       id: 4,
//       icon: <Printer className="w-8 h-8" />,
//       title: "Printing & Scanning",
//       description:
//         "High-quality technical printing, large-format outputs, and professional scanning services for all your documentation needs.",
//       shortDesc: "Professional printing & digitization",
//       color: "#F59E0B",
//       gradient: "from-orange-500 to-red-500",
//       features: [
//         "Large-Format Printing",
//         "CAD & Technical Drawings",
//         "Document Scanning",
//         "Digital Archiving",
//         "Custom Print Solutions",
//         "Quality Assurance",
//         "Fast Turnaround",
//       ],
//       stats: [
//         { value: "10K+", label: "Documents" },
//         { value: "A0-A4", label: "All Sizes" },
//         { value: "48h", label: "Delivery" },
//       ],
//     },
//   ];

//   // Auto-rotate services with direction tracking
//   React.useEffect(() => {
//     if (!autoPlay) return;

//     const interval = setInterval(() => {
//       setDirection(1);
//       setActiveService((prev) => (prev + 1) % services.length);
//     }, 5000);

//     return () => clearInterval(interval);
//   }, [autoPlay, services.length]);

//   const handleServiceChange = (index) => {
//     const newDirection = index > activeService ? 1 : -1;
//     setDirection(newDirection);
//     setActiveService(index);
//     setAutoPlay(false);
//   };

//   // Smooth container variants
//   const containerVariants = {
//     hidden: { opacity: 0 },
//     visible: {
//       opacity: 1,
//       transition: {
//         staggerChildren: 0.15,
//         delayChildren: 0.3,
//       },
//     },
//   };

//   // Ultra smooth item variants
//   const itemVariants = {
//     hidden: {
//       opacity: 0,
//       y: 40,
//       scale: 0.95,
//     },
//     visible: {
//       opacity: 1,
//       y: 0,
//       scale: 1,
//       transition: {
//         type: "spring",
//         stiffness: 100,
//         damping: 15,
//         duration: 0.8,
//       },
//     },
//   };

//   // Buttery smooth slide variants
//   const slideVariants = {
//     enter: (direction) => ({
//       x: direction > 0 ? 80 : -80,
//       opacity: 0,
//       scale: 0.98,
//       filter: "blur(4px)",
//     }),
//     center: {
//       zIndex: 1,
//       x: 0,
//       opacity: 1,
//       scale: 1,
//       filter: "blur(0px)",
//       transition: {
//         type: "spring",
//         stiffness: 200,
//         damping: 25,
//         duration: 0.8,
//       },
//     },
//     exit: (direction) => ({
//       zIndex: 0,
//       x: direction < 0 ? 80 : -80,
//       opacity: 0,
//       scale: 0.98,
//       filter: "blur(4px)",
//       transition: {
//         type: "spring",
//         stiffness: 200,
//         damping: 25,
//         duration: 0.6,
//       },
//     }),
//   };

//   // Feature item animation
//   const featureVariants = {
//     hidden: {
//       opacity: 0,
//       x: -30,
//       scale: 0.9,
//     },
//     visible: (i) => ({
//       opacity: 1,
//       x: 0,
//       scale: 1,
//       transition: {
//         type: "spring",
//         stiffness: 150,
//         damping: 15,
//         delay: i * 0.08,
//         duration: 0.6,
//       },
//     }),
//   };

//   // Stats item animation
//   const statVariants = {
//     hidden: {
//       opacity: 0,
//       scale: 0.8,
//       y: 20,
//     },
//     visible: (i) => ({
//       opacity: 1,
//       scale: 1,
//       y: 0,
//       transition: {
//         type: "spring",
//         stiffness: 120,
//         damping: 12,
//         delay: 0.4 + i * 0.1,
//         duration: 0.6,
//       },
//     }),
//   };

//   return (
//     <section
//       ref={sectionRef}
//       className="relative py-6 lg:py-12 bg-white overflow-hidden"
//     >
//       {/* Enhanced Animated Background */}
//       <div className="absolute inset-0">
//         <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-gray-50/80 to-transparent"></div>
//         <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-gray-50/80 to-transparent"></div>

//         {/* Smooth floating elements */}
//         <motion.div
//           animate={{
//             y: [0, -25, 0],
//             x: [0, 10, 0],
//             rotate: [0, 8, 0],
//           }}
//           transition={{
//             duration: 12,
//             repeat: Infinity,
//             ease: "easeInOut",
//           }}
//           className="absolute top-20 left-10 w-6 h-6 bg-brandBlue/15 rounded-full blur-sm"
//         />
//         <motion.div
//           animate={{
//             y: [0, 20, 0],
//             x: [0, -15, 0],
//             rotate: [0, -6, 0],
//           }}
//           transition={{
//             duration: 10,
//             repeat: Infinity,
//             ease: "easeInOut",
//             delay: 2,
//           }}
//           className="absolute top-40 right-20 w-4 h-4 bg-brandGreen/15 rounded-full blur-sm"
//         />
//         <motion.div
//           animate={{
//             y: [0, -18, 0],
//             scale: [1, 1.15, 1],
//           }}
//           transition={{
//             duration: 14,
//             repeat: Infinity,
//             ease: "easeInOut",
//             delay: 1,
//           }}
//           className="absolute bottom-40 left-1/4 w-3 h-3 bg-purple-500/15 rounded-full blur-sm"
//         />
//       </div>

//       <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
//         {/* Header Section with enhanced animations */}
//         <motion.div
//           initial={{ opacity: 0, y: 60 }}
//           animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
//           transition={{
//             type: "spring",
//             stiffness: 100,
//             damping: 20,
//             duration: 1,
//           }}
//           className="text-center max-w-4xl mx-auto mb-12"
//         >
//           <motion.div
//             initial={{ opacity: 0, scale: 0.8 }}
//             animate={
//               isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
//             }
//             transition={{ duration: 0.6, delay: 0.2 }}
//             className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 border border-brandBlue/20 mb-4"
//           >
//             <Cpu className="w-4 h-4 text-brandBlue mr-2" />
//             <span className="text-sm font-semibold text-brandText">
//               Our Services
//             </span>
//           </motion.div>

//           <motion.h2
//             initial={{ opacity: 0, y: 40 }}
//             animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
//             transition={{
//               type: "spring",
//               stiffness: 80,
//               damping: 20,
//               delay: 0.5,
//               duration: 1,
//             }}
//             className="text-3xl flex items-center justify-center gap-4 lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-8 font-lexend leading-tight"
//           >
//             Expert
//             <motion.span
//               initial={{ opacity: 0, scale: 0.9 }}
//               animate={
//                 isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }
//               }
//               transition={{
//                 type: "spring",
//                 stiffness: 100,
//                 damping: 15,
//                 delay: 0.7,
//                 duration: 0.8,
//               }}
//               className="block bg-gradient-to-r from-brandBlue to-brandGreen bg-clip-text text-transparent"
//             >
//               Solutions
//             </motion.span>
//           </motion.h2>

//           <p className="text-lg lg:text-xl text-secondaryTextColor leading-relaxed">
//             Comprehensive services designed to transform your vision into
//             reality with innovation and precision.
//           </p>
//         </motion.div>

//         {/* Main Content - Enhanced Interactive Service Showcase */}
//         <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
//           {/* Service Navigation */}
//           <motion.div
//             variants={containerVariants}
//             initial="hidden"
//             animate={isInView ? "visible" : "hidden"}
//             className="lg:col-span-4 space-y-4"
//           >
//             {services.map((service, index) => (
//               <motion.button
//                 key={service.id}
//                 variants={itemVariants}
//                 custom={index}
//                 onClick={() => handleServiceChange(index)}
//                 className={`w-full p-6 rounded-2xl text-left transition-all duration-700 group ${
//                   activeService === index
//                     ? "bg-white shadow-2xl border-2 scale-[1.02]"
//                     : "bg-gray-50/60 hover:bg-white hover:shadow-xl border border-transparent hover:scale-[1.01]"
//                 } backdrop-blur-sm`}
//                 style={{
//                   borderColor:
//                     activeService === index ? service.color : "transparent",
//                   transformOrigin: "left center",
//                 }}
//                 whileHover={{
//                   scale: activeService === index ? 1.02 : 1.01,
//                   transition: { type: "spring", stiffness: 300, damping: 20 },
//                 }}
//               >
//                 <div className="flex items-center space-x-4">
//                   <motion.div
//                     className={`w-14 h-14 rounded-xl flex items-center justify-center text-white transition-all duration-700 ${
//                       activeService === index
//                         ? "scale-110 shadow-xl"
//                         : "shadow-lg"
//                     }`}
//                     style={{
//                       background:
//                         activeService === index
//                           ? `linear-gradient(135deg, ${service.color}, ${service.color}dd)`
//                           : `linear-gradient(135deg, ${service.color}40, ${service.color}30)`,
//                     }}
//                     whileHover={{
//                       scale: activeService === index ? 1.15 : 1.05,
//                       rotate: activeService === index ? 0 : 5,
//                     }}
//                     transition={{ type: "spring", stiffness: 200, damping: 15 }}
//                   >
//                     {service.icon}
//                   </motion.div>
//                   <div className="flex-1 min-w-0">
//                     <motion.h3
//                       className={`text-xl font-bold font-lexend transition-colors duration-500 truncate ${
//                         activeService === index
//                           ? "text-primaryTextColor"
//                           : "text-secondaryTextColor"
//                       }`}
//                     >
//                       {service.title}
//                     </motion.h3>
//                     <motion.p
//                       className={`text-sm mt-1 transition-colors duration-500 text-ellipsis overflow-hidden ${
//                         activeService === index
//                           ? "text-secondaryTextColor"
//                           : "text-gray-500"
//                       }`}
//                     >
//                       {service.shortDesc}
//                     </motion.p>
//                   </div>
//                   <motion.div
//                     animate={{
//                       x: activeService === index ? 4 : 0,
//                       rotate: activeService === index ? 90 : 0,
//                     }}
//                     transition={{ type: "spring", stiffness: 200, damping: 15 }}
//                   >
//                     <ChevronRight
//                       className={`w-5 h-5 transition-colors duration-500 ${
//                         activeService === index
//                           ? "text-brandBlue"
//                           : "text-gray-400 group-hover:text-brandBlue"
//                       }`}
//                     />
//                   </motion.div>
//                 </div>
//               </motion.button>
//             ))}
//           </motion.div>

//           {/* Service Details with enhanced smoothness */}
//           <motion.div
//             initial={{ opacity: 0, x: 60 }}
//             animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
//             transition={{
//               type: "spring",
//               stiffness: 80,
//               damping: 20,
//               delay: 0.6,
//               duration: 1,
//             }}
//             className="lg:col-span-8"
//           >
//             <AnimatePresence mode="wait" custom={direction}>
//               <motion.div
//                 key={activeService}
//                 custom={direction}
//                 variants={slideVariants}
//                 initial="enter"
//                 animate="center"
//                 exit="exit"
//                 className="bg-white rounded-3xl shadow-2xl border border-gray-200/80 overflow-hidden backdrop-blur-sm"
//               >
//                 <div className="p-8 lg:p-12">
//                   {/* Service Header */}
//                   <div className="flex items-center justify-between mb-8">
//                     <div className="flex items-center space-x-4">
//                       <motion.div
//                         className="w-16 h-16 rounded-2xl flex items-center justify-center text-white shadow-2xl"
//                         style={{
//                           background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}dd)`,
//                         }}
//                         animate={{
//                           scale: [1, 1.05, 1],
//                           rotate: [0, 5, 0],
//                         }}
//                         transition={{
//                           duration: 4,
//                           repeat: Infinity,
//                           ease: "easeInOut",
//                         }}
//                       >
//                         {services[activeService].icon}
//                       </motion.div>
//                       <div>
//                         <motion.h3
//                           className="text-3xl lg:text-4xl font-bold text-primaryTextColor font-lexend"
//                           initial={{ opacity: 0, y: 20 }}
//                           animate={{ opacity: 1, y: 0 }}
//                           transition={{ duration: 0.6 }}
//                         >
//                           {services[activeService].title}
//                         </motion.h3>
//                         <motion.p
//                           className="text-xl text-secondaryTextColor mt-2"
//                           initial={{ opacity: 0, y: 15 }}
//                           animate={{ opacity: 1, y: 0 }}
//                           transition={{ duration: 0.6, delay: 0.1 }}
//                         >
//                           {services[activeService].shortDesc}
//                         </motion.p>
//                       </div>
//                     </div>

//                     {/* Auto-play control */}
//                     <motion.button
//                       onClick={() => setAutoPlay(!autoPlay)}
//                       className="p-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-500"
//                       whileHover={{ scale: 1.1 }}
//                       whileTap={{ scale: 0.9 }}
//                     >
//                       {autoPlay ? (
//                         <Pause className="w-5 h-5" />
//                       ) : (
//                         <Play className="w-5 h-5" />
//                       )}
//                     </motion.button>
//                   </div>

//                   {/* Description */}
//                   <motion.p
//                     className="text-lg lg:text-xl text-secondaryTextColor leading-relaxed mb-8"
//                     initial={{ opacity: 0 }}
//                     animate={{ opacity: 1 }}
//                     transition={{ duration: 0.8, delay: 0.2 }}
//                   >
//                     {services[activeService].description}
//                   </motion.p>

//                   <div className="grid lg:grid-cols-2 gap-8">
//                     {/* Features */}
//                     <div>
//                       <motion.h4
//                         className="text-xl font-bold text-primaryTextColor mb-6 font-lexend flex items-center"
//                         initial={{ opacity: 0, x: -20 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.6, delay: 0.3 }}
//                       >
//                         <Target className="w-5 h-5 text-brandBlue mr-2" />
//                         Key Features
//                       </motion.h4>
//                       <div className="space-y-3">
//                         {services[activeService].features.map(
//                           (feature, index) => (
//                             <motion.div
//                               key={index}
//                               custom={index}
//                               variants={featureVariants}
//                               initial="hidden"
//                               animate="visible"
//                               className="flex items-center space-x-3 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white hover:shadow-lg transition-all duration-500 group cursor-pointer"
//                               whileHover={{
//                                 scale: 1.02,
//                                 x: 5,
//                                 transition: {
//                                   type: "spring",
//                                   stiffness: 300,
//                                   damping: 20,
//                                 },
//                               }}
//                             >
//                               <motion.div
//                                 className="w-2 h-2 bg-brandBlue rounded-full flex-shrink-0"
//                                 whileHover={{ scale: 1.5 }}
//                                 transition={{
//                                   type: "spring",
//                                   stiffness: 400,
//                                   damping: 10,
//                                 }}
//                               />
//                               <span className="text-secondaryTextColor font-medium">
//                                 {feature}
//                               </span>
//                             </motion.div>
//                           )
//                         )}
//                       </div>
//                     </div>

//                     {/* Stats */}
//                     <div>
//                       <motion.h4
//                         className="text-xl font-bold text-primaryTextColor mb-6 font-lexend flex items-center"
//                         initial={{ opacity: 0, x: 20 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.6, delay: 0.3 }}
//                       >
//                         <Zap className="w-5 h-5 text-brandGreen mr-2" />
//                         Performance Metrics
//                       </motion.h4>
//                       <div className="grid grid-cols-3 gap-4">
//                         {services[activeService].stats.map((stat, index) => (
//                           <motion.div
//                             key={index}
//                             custom={index}
//                             variants={statVariants}
//                             initial="hidden"
//                             animate="visible"
//                             className="text-center p-4 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:shadow-xl transition-all duration-500 cursor-pointer"
//                             whileHover={{
//                               scale: 1.05,
//                               y: -2,
//                               transition: {
//                                 type: "spring",
//                                 stiffness: 300,
//                                 damping: 20,
//                               },
//                             }}
//                           >
//                             <div className="text-2xl font-bold text-primaryTextColor mb-1">
//                               {stat.value}
//                             </div>
//                             <div className="text-sm text-secondaryTextColor font-medium">
//                               {stat.label}
//                             </div>
//                           </motion.div>
//                         ))}
//                       </div>

//                       {/* CTA Button */}
//                       <motion.button
//                         initial={{ opacity: 0, y: 30 }}
//                         animate={{ opacity: 1, y: 0 }}
//                         transition={{
//                           type: "spring",
//                           stiffness: 150,
//                           damping: 15,
//                           delay: 0.8,
//                           duration: 0.6,
//                         }}
//                         className="w-full mt-6 py-4 text-white font-semibold rounded-xl hover:shadow-2xl transition-all duration-500 transform text-lg relative overflow-hidden group"
//                         style={{
//                           background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}dd)`,
//                         }}
//                         whileHover={{
//                           scale: 1.02,
//                           y: -2,
//                         }}
//                         whileTap={{ scale: 0.98 }}
//                       >
//                         <motion.span
//                           className="relative z-10"
//                           animate={{ x: [0, 5, 0] }}
//                           transition={{
//                             duration: 2,
//                             repeat: Infinity,
//                             repeatDelay: 3,
//                           }}
//                         >
//                           Explore {services[activeService].title}
//                         </motion.span>
//                         <motion.div
//                           className="absolute inset-0 bg-white/20"
//                           initial={{ x: "-100%" }}
//                           whileHover={{ x: "100%" }}
//                           transition={{ duration: 0.6 }}
//                         />
//                       </motion.button>
//                     </div>
//                   </div>
//                 </div>
//               </motion.div>
//             </AnimatePresence>
//           </motion.div>
//         </div>

//         {/* Enhanced Bottom Navigation Dots */}
//         <motion.div
//           initial={{ opacity: 0, y: 40 }}
//           animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
//           transition={{
//             type: "spring",
//             stiffness: 100,
//             damping: 20,
//             delay: 1.2,
//             duration: 0.8,
//           }}
//           className="flex justify-center space-x-4 mt-12"
//         >
//           {services.map((service, index) => (
//             <motion.button
//               key={index}
//               onClick={() => handleServiceChange(index)}
//               className={`relative rounded-full transition-all duration-500 ${
//                 activeService === index ? "scale-110" : "hover:scale-105"
//               }`}
//               whileHover={{ scale: 1.1 }}
//               whileTap={{ scale: 0.9 }}
//             >
//               <div
//                 className={`w-3 h-3 rounded-full transition-all duration-500 ${
//                   activeService === index
//                     ? "bg-brandBlue"
//                     : "bg-gray-300 hover:bg-gray-400"
//                 }`}
//               />
//               {activeService === index && (
//                 <motion.div
//                   className="absolute inset-0 border-2 border-brandBlue rounded-full"
//                   initial={{ scale: 1.5, opacity: 0 }}
//                   animate={{ scale: 1, opacity: 1 }}
//                   transition={{ type: "spring", stiffness: 300, damping: 20 }}
//                 />
//               )}
//             </motion.button>
//           ))}
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// export default ServicesOverview;
